import React from "react";
import ApiService from "../services/ApiService";
import AppService from "../services/AppService";
import {Button, Modal, QRCode, Typography} from "antd";

import {CloseOutlined} from "@ant-design/icons"

import Lottie from 'react-lottie';
import * as animationCardTpe from '../assets/lottie/card-tpe.json'
import {connect} from "react-redux";


const {Title, Text} = Typography


const PAIEMENT_INIT_TIMEOUT = 1 * 60 * 1000
const LINK_INIT_TIMEOUT = 2 * 60 * 1000

class PaiementModal extends React.Component {

    constructor(props) {
        super(props);
        if (props.getRef) {
            props.getRef(this)
        }
    }

    initialState = {
        show: false,
        link: null,
        paiement: null,
        processing: false,
        text: null,
        errored: false
    }

    state = this.initialState

    _mounted = true

    safeSetState(partial) {
        if (!this._mounted) return
        try {
            this.setState(partial)
        } catch (e) {
            console.error("safeSetState failed", e)
        }
    }

    clearTimeouts(){
        clearTimeout(this.closeTimeout)
        clearTimeout(this.linkTimeout)
        clearTimeout(this.paiementTimeout)
    }

    stopServerPolling() {
        try {
            this.getIntervalStatusRef && this.getIntervalStatusRef()
        } catch (e) {
            console.error("stopServerPolling failed", e)
        }
        this.getIntervalStatusRef = null
    }

    init(config) {
        this.clearTimeouts()
        this.stopServerPolling()
        this.setState({...this.initialState, show: true})
        this.makePaiement(config)
    }

    componentWillUnmount() {
        this._mounted = false
        this.clearTimeouts()
        this.stopServerPolling()
    }

    onClose() {
        this.clearTimeouts()
        this.stopServerPolling()

        const hadLink = !!this.state.link
        this.safeSetState(this.initialState)
        if (hadLink && this.props.onFinish) {
            try {
                this.props.onFinish()
            } catch (e) {
                console.error("onFinish failed", e)
            }
        }
    }


    async makePaiement(config) {
        clearTimeout(this.paiementTimeout)
        this.paiementTimeout = setTimeout(() => {
            console.log("PAIEMENT SESSION TIMEOUT")
            this.cancelPaiement()
        }, PAIEMENT_INIT_TIMEOUT)

        this.safeSetState({text: "Initialisation du paiement..."})

        const borneId = window.BORNEID
        let apiResult;
        try {
            apiResult = await ApiService.post("/paiement/init/" + borneId, config)
        } catch (e) {
            console.error("paiement/init failed", e)
            this.safeSetState({errored: true, text: "Erreur d'initialisation : " + (e?.message || e)})
            this.closeTimeout = setTimeout(() => this.onClose(), 5 * 1000)
            return
        }

        if (!apiResult || !apiResult.success || !apiResult.paiementId) {
            console.error("paiement/init unsuccessful", apiResult)
            this.safeSetState({errored: true, text: "Erreur d'initialisation : " + (apiResult?.error || "Réponse invalide")})
            this.closeTimeout = setTimeout(() => this.onClose(), 5 * 1000)
            return
        }

        if (!this._mounted) return

        this.safeSetState({text: "Vous pouvez utiliser votre carte sur le terminal."})

        // Race bridge natif vs polling serveur.
        // Le polling serveur est la source de vérité (webhook Stripe) et sert de filet
        // de sécurité si le postMessage du bridge est perdu (WebView en reload, ref stale,
        // SEND ERROR, SESSION TIMEOUT...).
        // - Si le bridge gagne en succès → instantané, comportement historique.
        // - Si le bridge gagne en échec "métier" (carte refusée) → on affiche l'erreur.
        // - Si le bridge échoue en transport (SEND ERROR, SESSION TIMEOUT, etc.) → on
        //   attend silencieusement que le polling serveur tranche.
        // - Si le polling gagne en succès → on continue ; le bridge expirera tout seul.

        const paiementId = apiResult.paiementId
        const isBridgeTransportError = (errStr) => {
            if (typeof errStr !== "string") return false
            return errStr.includes("SEND ERROR")
                || errStr.includes("SESSION TIMEOUT")
                || errStr.includes("SESSION_TIMEOUT")
        }

        let finalResult = null
        let finalSource = null

        try {
            // Polling serveur — démarre toujours, même en mode natif.
            const serverPromise = ApiService.getInterval(
                "/paiement/webStatus/" + paiementId,
                null,
                (ref => this.getIntervalStatusRef = ref)
            ).then(
                result => ({source: "server", result, ok: true}),
                error => ({source: "server", result: null, ok: false, error})
            )

            // Bridge natif — uniquement en mode borne.
            const bridgePromise = !window.ISWEB
                ? AppService.get("terminal.paiement", {secret: apiResult.secret}).then(
                    result => ({source: "bridge", result, ok: true}),
                    error => ({source: "bridge", result: null, ok: false, error})
                )
                : null

            let pending = bridgePromise ? [bridgePromise, serverPromise] : [serverPromise]

            // Première résolution du race
            let first = await Promise.race(pending)
            console.log("paiement race first", first.source, first.result, first.result?.error)

            // Si le bridge gagne en premier mais que c'est une erreur de transport
            // (postMessage perdu, timeout d'ACK 60s, etc.), on l'ignore et on attend
            // la décision du serveur. Le paiement peut quand même avoir réussi côté Stripe.
            if (first.source === "bridge" && first.ok && first.result && !first.result.success) {
                const rawErr = first.result.error
                const errStr = typeof rawErr === "string" ? rawErr : (rawErr?.message || "")
                if (isBridgeTransportError(errStr)) {
                    console.log("paiement bridge transport error — waiting for server", errStr)
                    // On attend le serveur (qui n'a pas encore résolu sinon il aurait gagné).
                    first = await serverPromise
                    console.log("paiement server (after bridge transport error)", first.result, first.result?.error)
                }
            }

            // Si la promesse retenue est elle-même une exception (ok=false), on la traite
            // comme un échec serveur ou un bridge crashé. On ne crashe pas la modal.
            if (!first.ok) {
                console.error("paiement race threw", first.source, first.error)
                // Si c'est le bridge qui a thrown et qu'on est en mode natif, on attend le serveur.
                if (first.source === "bridge") {
                    first = await serverPromise
                    console.log("paiement server (after bridge throw)", first.result, first.result?.error)
                }
            }

            finalResult = first.result
            finalSource = first.source
        } catch (e) {
            console.error("paiement race crashed", e)
            this.stopServerPolling()
            this.safeSetState({errored: true, text: "Erreur inattendue : " + (e?.message || e)})
            this.closeTimeout = setTimeout(() => this.onClose(), 5 * 1000)
            return
        }

        if (!this._mounted) return

        // À ce stade, finalResult est soit le résultat du bridge (succès ou erreur métier),
        // soit le résultat du polling serveur.
        if (finalSource === "bridge") {
            if (!finalResult || !finalResult.success) {
                const errStr = typeof finalResult?.error === "string"
                    ? finalResult.error
                    : (finalResult?.error?.message || JSON.stringify(finalResult?.error || "ERR"))

                if (typeof errStr === "string" && errStr.includes("NETWORK")) {
                    this.stopServerPolling()
                    return await AppService.get("app.restart")
                }

                this.stopServerPolling()
                this.safeSetState({errored: true, text: "Terminal :\n" + errStr})
                this.closeTimeout = setTimeout(() => {
                    console.log("CLOSE TIMEOUT")
                    this.onClose()
                }, 5 * 1000)
                return false
            }
            // Bridge succès → on stoppe le polling, paiement OK.
            this.stopServerPolling()
        } else {
            // Polling serveur (mode web pur ou rattrapage en mode natif).
            if (!finalResult || !finalResult.success) {
                const errStr = typeof finalResult?.error === "string"
                    ? finalResult.error
                    : (finalResult?.error?.message || "ERROR")
                this.safeSetState({errored: true, text: errStr + (finalResult?.message ? ":\n" + finalResult.message : "")})

                setTimeout(() => {
                    this.cancelPaiement()
                    this.onClose()
                }, 5 * 1000)
                return false
            }
            // Polling serveur succès → on continue. On ne cancel pas le bridge :
            // si le paiement a réussi côté Stripe, on ne veut surtout pas le révoquer.
        }

        if (!this._mounted) return

        this.safeSetState({processing: true})
        clearTimeout(this.paiementTimeout)

        let postApiResult;
        try {
            postApiResult = await ApiService.get("/post-paiement/link/" + apiResult.paiementId)
        } catch (e) {
            console.error("post-paiement/link failed", e)
            this.safeSetState({errored: true, text: "Erreur post-paiement : " + (e?.message || e)})
            this.closeTimeout = setTimeout(() => this.onClose(), 5 * 1000)
            return
        }

        if (!postApiResult || !postApiResult.link) {
            console.error("post-paiement/link missing link", postApiResult)
            this.safeSetState({errored: true, text: "Erreur post-paiement"})
            this.closeTimeout = setTimeout(() => this.onClose(), 5 * 1000)
            return
        }

        this.safeSetState({link: postApiResult.link, paiement: postApiResult.paiement})

        this.linkTimeout = setTimeout(() => {
            console.log("LINK SESSION TIMEOUT")
            this.onClose()
        }, LINK_INIT_TIMEOUT)
    }


    async cancelPaiement() {
        // On stoppe le polling tout de suite pour éviter qu'il continue à interroger
        // le serveur pendant que l'annulation est en cours.
        this.stopServerPolling()

        try {
            if (!window.ISWEB) {
                const res = await AppService.get("terminal.cancel")
                console.log("cancelPaiement bridge", res)
                if (res && res.success) {
                    this.onClose()
                    return
                }
            } else {
                const borneId = window.BORNEID
                const res = await ApiService.get("/paiement/webCancel/" + borneId)
                console.log("cancelWebPaiement", res)
                if (res && res.success) {
                    this.onClose()
                    return
                }
            }
        } catch (e) {
            console.error("cancelPaiement failed", e)
        }

        // Si l'annulation n'a pas réussi, on ferme quand même la modal après un court délai
        // pour ne pas laisser l'utilisateur bloqué.
        this.closeTimeout = setTimeout(() => this.onClose(), 2 * 1000)
    }


    render() {

        return (
            <Modal open={this.state.show} closable={false} footer={null}>
                <div style={{height: "70vh"}}>

                    {!this.state.link && (
                        <div style={{
                            display: "flex",
                            flex: 1,
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                        }}>

                            <Title level={5} style={{textAlign: "center", height: "10vh"}}>{this.state.text}</Title>


                            {!this.state.errored && (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    border: "0px solid red",
                                    height: "50vh"
                                }}>
                                    <Lottie
                                        options={{
                                            loop: true,
                                            autoplay: true,
                                            animationData: animationCardTpe,
                                            rendererSettings: {
                                                preserveAspectRatio: 'xMidYMid slice'
                                            }
                                        }}
                                        height={250}
                                        width={"100%"}
                                    />
                                </div>
                            )}


                            {this.state.errored && (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    border: "0px solid red",
                                    height: "50vh"
                                }}>
                                    <img
                                        alt=""
                                        src={'/assets/image/close.png'}
                                        style={{height: 150, width: "100%"}}
                                    />
                                </div>
                            )}


                            {!this.state.processing && (
                                <Button onClick={() => this.cancelPaiement()} danger style={{overflow:"hidden"}}>{this.state.errored?"Terminer":"Annuler"}</Button>
                            )}
                        </div>
                    )}


                    {this.state.link && (
                        <div style={{
                            display: "flex",
                            flex: 1,
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                        }}>

                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                border: "0px solid red",
                                height: "20vh",
                                textAlign: "center"
                            }}>
                                <Title level={4}>La suite, c'est sur votre téléphone !</Title>

                                <Text>Afin
                                    de {this.state.paiement?.cerfa ? "recevoir votre Reçu" : "completer votre paiement"},
                                    scannez ce QR-code sur l'application « Appareil photo » de votre téléphone.</Text>
                            </div>

                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                border: "0px solid red", height: "45vh"
                            }}>
                                <QRCode
                                    errorLevel={"H"}
                                    value={this.state.link}
                                    size={250}
                                    iconSize={250 / 3}
                                    icon={this.props.org?.logo}
                                />
                            </div>

                            <Button onClick={() => this.onClose()} type={"primary"}>Terminer</Button>
                        </div>
                    )}

                </div>
            </Modal>
        )
    }
}

const mapStateToProps = (state) => {
    return {
        org: state.data.org
    }
}

export default connect(mapStateToProps)(PaiementModal)
